<template>
  <div class="card mb-4">

    <!-- UPDATED: Added flex-column and gap for mobile responsiveness -->
    <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
      <h6 class="mb-0">Roles</h6>
      <div class="d-flex align-items-center w-100 w-md-auto justify-content-between justify-content-md-end">
        <!-- VIEW TOGGLE BUTTONS -->
        <div class="btn-group btn-group-sm me-3 shadow-sm">
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'table' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'card' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'card'"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <button type="button" class="btn btn-theme-primary mb-0" @click="showModal('add')">
          {{ $t('add_role') }}
        </button>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      
      <!-- 1. TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">{{
                $t('role_name') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('status') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(role, index) in paginatedRoles" :key="index">
              <td class="text-left">
               <p class="text-xs font-weight-bold mb-0 text-dark">{{ role.name }}</p>

              </td>
              <td class="text-left">
                <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">{{
                  role.status == 1 ? 'Active' : "InActive" }}</soft-badge>
              </td>
              <td class="text-left">
                <a href="javascript:;" class="text-secondary font-weight-bold text-xs"
                  @click="showModal('edit', (currentPage - 1) * itemsPerPage + index)">
                  <i class="fas fa-edit"></i>
                </a>
                <a v-if="!isProtectedFromDelete(role)" href="javascript:;"
                  class="text-danger font-weight-bold text-xs ms-3" @click="confirmDelete((currentPage - 1) * itemsPerPage + index, role.id)">
                  <i class="fas fa-trash-alt"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2. CARD VIEW -->
      <div v-else class="px-3 px-md-4 pb-4 pt-3">
        <div class="row">
          <!-- UPDATED: Added col-12 for full width on mobile -->
          <div v-for="(role, index) in paginatedRoles" :key="'card-' + index" class="col-12 col-md-6 col-xl-3 mb-4">
            <div class="card h-100 border shadow-sm">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-center overflow-hidden">
                    <!-- Updated Icon Shape to use Theme Variable -->
                    <div class="icon-shape icon-sm shadow text-center border-radius-md d-flex align-items-center justify-content-center me-2 theme-icon-bg flex-shrink-0" style="width: 32px; height: 32px;">
                      <i class="fas fa-user-shield text-white text-xs opacity-10"></i>
                    </div>
                   <h6 class="mb-0 text-sm text-dark text-truncate">{{ role.name }}</h6>
                  </div>
                  <div class="d-flex">
                    <a href="javascript:;" class="text-secondary me-3" @click="showModal('edit', (currentPage - 1) * itemsPerPage + index)" title="Edit Role">
                      <i class="fas fa-edit text-xs"></i>
                    </a>
                    <a v-if="!isProtectedFromDelete(role)" href="javascript:;" class="text-danger" @click="confirmDelete((currentPage - 1) * itemsPerPage + index, role.id)" title="Delete Role">
                      <i class="fas fa-trash-alt text-xs"></i>
                    </a>
                  </div>
                </div>
                
                <div class="d-flex justify-content-between align-items-center pt-3 border-top">
                  <span class="text-xxs text-uppercase text-secondary font-weight-bolder">{{ $t('status') }}</span>
                  <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">
                    {{ role.status == 1 ? 'Active' : "InActive" }}
                  </soft-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Logic -->
      <div v-if="roles.length === 0" class="text-center py-4 text-secondary text-sm">
        No roles found.
      </div>

      <!-- UPDATED: flex-wrap for mobile pagination -->
      <div class="pagination flex-wrap">
        <button @click="prevPage" :disabled="currentPage === 1">{{ $t('previous') }}</button>
        <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
          :class="{ active: currentPage === page }">
          {{ page }}
        </button>
        <button @click="nextPage" :disabled="currentPage === totalPages">{{ $t('next') }}</button>
      </div>
    </div>

    <!-- Add Role Modal -->
    <div v-if="isAddModalVisible" class="modal fade show" style="display: block;" tabindex="-1" role="dialog"
      aria-labelledby="addRoleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered px-3" style="max-width: 900px;" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addRoleModalLabel">{{ $t('add_role') }}</h5>
            <button type="button" class="close btn-modal-close" @click="hideModal('add')" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" style="max-height: 85vh; overflow-y: auto;">
            <form @submit.prevent="addRole">
              <div class="form-group">
                <label for="roleName">{{ $t('role_name') }}</label>
                <input type="text" class="form-control" id="roleName" v-model="newRole.name" required>
                <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
              </div>

              <!-- INSTALLATION TYPE SELECTOR (Visible for Super Admin with multi-select & common toggle, hidden for tenant admin) -->
              <div v-if="isSuperAdmin" class="form-group my-3 p-3 bg-light rounded border">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="font-weight-bold text-dark text-sm mb-0">Select Installation Types</label>
                  <div class="form-check form-switch mb-0">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="addIsCommon"
                      v-model="newRole.is_common" 
                      :true-value="1" 
                      :false-value="0"
                    />
                    <label class="form-check-label text-xs fw-bold text-primary" for="addIsCommon">
                      Common for All Types
                    </label>
                  </div>
                </div>

                <div v-if="!newRole.is_common" class="d-flex flex-wrap gap-3 mt-2">
                  <div 
                    v-for="type in installationTypes" 
                    :key="type.id" 
                    class="form-check me-3 mb-2"
                  >
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :id="'add_inst_type_' + type.id" 
                      :value="type.id" 
                      v-model="newRole.selected_installation_type_ids"
                      @change="onInstallationTypesChange(newRole.selected_installation_type_ids)"
                    />
                    <label class="form-check-label text-xs font-weight-bold" :for="'add_inst_type_' + type.id">
                      {{ getTypeName(type) }}
                    </label>
                  </div>
                </div>
                <div v-else class="text-xs text-muted italic mt-1">
                  <i class="fas fa-info-circle me-1 text-primary"></i> This role will be shared across all installation types.
                </div>
              </div>
              <input v-else type="hidden" :value="userInstallationTypeId" />

              <div class="form-group my-3">
                <label class="font-weight-bold mb-2">{{ $t('Select_modules') || 'Module Permissions & Action Scopes' }}</label>
                <div class="row">
                  <div class="col-12 col-md-6 mb-3" v-for="mod in modules" :key="mod.id || mod.module_id || mod">
                    <div class="card border p-3 h-100 shadow-none">
                      <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="fw-bold text-dark text-sm">{{ getModuleDisplayName(mod) }}</span>
                        <button 
                          type="button" 
                          class="btn btn-xs mb-0 py-1 px-2" 
                          :class="isModuleFullyChecked(mod) ? 'btn-soft-primary' : 'btn-outline-secondary'"
                          @click="toggleAllModuleActions(mod)"
                        >
                          {{ isModuleFullyChecked(mod) ? 'Deselect All' : 'Select All' }}
                        </button>
                      </div>
                      <div class="d-flex flex-wrap gap-2">
                        <div class="form-check form-check-inline me-2">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'add_' + getModuleCode(mod) + '_read'"
                            :checked="isActionChecked(mod, 'read')"
                            @change="toggleAction(mod, 'read')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'add_' + getModuleCode(mod) + '_read'">View/List</label>
                        </div>
                        <div class="form-check form-check-inline me-2">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'add_' + getModuleCode(mod) + '_create'"
                            :checked="isActionChecked(mod, 'create')"
                            @change="toggleAction(mod, 'create')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'add_' + getModuleCode(mod) + '_create'">Add/Create</label>
                        </div>
                        <div class="form-check form-check-inline me-2">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'add_' + getModuleCode(mod) + '_update'"
                            :checked="isActionChecked(mod, 'update')"
                            @change="toggleAction(mod, 'update')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'add_' + getModuleCode(mod) + '_update'">Edit/Update</label>
                        </div>
                        <div class="form-check form-check-inline">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'add_' + getModuleCode(mod) + '_delete'"
                            :checked="isActionChecked(mod, 'delete')"
                            @change="toggleAction(mod, 'delete')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'add_' + getModuleCode(mod) + '_delete'">Delete</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div class="form-group d-flex align-items-center justify-content-center">
                <label class="mr-2">Inactive</label>
                <label class="switch">
                  <input type="checkbox" v-model="newRole.status" :true-value="'1'" :false-value="'0'">
                  <span class="slider round"></span>
                </label>
                <label class="ml-2">Active</label>
              </div>
              <button type="submit" class="btn btn-theme-primary w-100 mt-2">
                {{ $t('add_role') }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Role Modal -->
    <div v-if="isEditModalVisible" class="modal fade show" style="display: block;" tabindex="-1" role="dialog"
      aria-labelledby="editRoleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered px-3" style="max-width: 900px;" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editRoleModalLabel">{{ $t('update_role') }}</h5>
            <button type="button" class="close btn-modal-close" @click="hideModal('edit')" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" style="max-height: 85vh; overflow-y: auto;">
            <form @submit.prevent="updateRole">
              <div class="form-group">
                <label for="editRoleName">{{ $t('role_name') }}</label>
                <input type="text" class="form-control" id="editRoleName" v-model="editedRole.name" required>
                <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
              </div>

              <!-- INSTALLATION TYPE SELECTOR (Visible for Super Admin with multi-select & common toggle, hidden for tenant admin) -->
              <div v-if="isSuperAdmin" class="form-group my-3 p-3 bg-light rounded border">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="font-weight-bold text-dark text-sm mb-0">Select Installation Types</label>
                  <div class="form-check form-switch mb-0">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="editIsCommon"
                      v-model="editedRole.is_common" 
                      :true-value="1" 
                      :false-value="0"
                    />
                    <label class="form-check-label text-xs fw-bold text-primary" for="editIsCommon">
                      Common for All Types
                    </label>
                  </div>
                </div>

                <div v-if="!editedRole.is_common" class="d-flex flex-wrap gap-3 mt-2">
                  <div 
                    v-for="type in installationTypes" 
                    :key="type.id" 
                    class="form-check me-3 mb-2"
                  >
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :id="'edit_inst_type_' + type.id" 
                      :value="type.id" 
                      v-model="editedRole.selected_installation_type_ids"
                      @change="onInstallationTypesChange(editedRole.selected_installation_type_ids)"
                    />
                    <label class="form-check-label text-xs font-weight-bold" :for="'edit_inst_type_' + type.id">
                      {{ getTypeName(type) }}
                    </label>
                  </div>
                </div>
                <div v-else class="text-xs text-muted italic mt-1">
                  <i class="fas fa-info-circle me-1 text-primary"></i> This role will be shared across all installation types.
                </div>
              </div>
              <input v-else type="hidden" :value="userInstallationTypeId" />

              <div class="form-group my-3">
                <label class="font-weight-bold mb-2">{{ $t('Select_modules') || 'Module Permissions & Action Scopes' }}</label>
                <div class="row">
                  <div class="col-12 col-md-6 mb-3" v-for="mod in modules" :key="mod.id || mod.module_id || mod">
                    <div class="card border p-3 h-100 shadow-none">
                      <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="fw-bold text-dark text-sm">{{ getModuleDisplayName(mod) }}</span>
                        <button 
                          type="button" 
                          class="btn btn-xs mb-0 py-1 px-2" 
                          :class="isModuleFullyChecked(mod) ? 'btn-soft-primary' : 'btn-outline-secondary'"
                          @click="toggleAllModuleActions(mod)"
                        >
                          {{ isModuleFullyChecked(mod) ? 'Deselect All' : 'Select All' }}
                        </button>
                      </div>
                      <div class="d-flex flex-wrap gap-2">
                        <div class="form-check form-check-inline me-2">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'edit_' + getModuleCode(mod) + '_read'"
                            :checked="isActionChecked(mod, 'read')"
                            @change="toggleAction(mod, 'read')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'edit_' + getModuleCode(mod) + '_read'">View/List</label>
                        </div>
                        <div class="form-check form-check-inline me-2">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'edit_' + getModuleCode(mod) + '_create'"
                            :checked="isActionChecked(mod, 'create')"
                            @change="toggleAction(mod, 'create')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'edit_' + getModuleCode(mod) + '_create'">Add/Create</label>
                        </div>
                        <div class="form-check form-check-inline me-2">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'edit_' + getModuleCode(mod) + '_update'"
                            :checked="isActionChecked(mod, 'update')"
                            @change="toggleAction(mod, 'update')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'edit_' + getModuleCode(mod) + '_update'">Edit/Update</label>
                        </div>
                        <div class="form-check form-check-inline">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="'edit_' + getModuleCode(mod) + '_delete'"
                            :checked="isActionChecked(mod, 'delete')"
                            @change="toggleAction(mod, 'delete')"
                          >
                          <label class="form-check-label text-xs fw-normal" :for="'edit_' + getModuleCode(mod) + '_delete'">Delete</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div class="form-group d-flex align-items-center justify-content-center">
                <label class="mr-2">Inactive</label>
                <label class="switch">
                  <input type="checkbox" v-model="editedRole.status" :true-value="'1'" :false-value="'0'">
                  <span class="slider round"></span>
                </label>
                <label class="ml-2">Active</label>
              </div>
              <button type="submit" class="btn btn-theme-primary w-100 mt-2">
                {{ $t('update_role') }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>
<script>
import SoftBadge from "@/components/SoftBadge.vue";
import '@fortawesome/fontawesome-free/css/all.css';
import RolesDataService from "../../services/RolesDataService.js";
import axios from 'axios';
import Swal from 'sweetalert2';
import { hasPermission } from "@/utils/permissions.js";

export default {
  name: "RoleTable",
  components: {
    SoftBadge,
  },
  data() {
    return {
      viewMode: 'card', 
      roles: [],
      installationTypes: [],
      allModulesCache: [],
      // Renamed User to MediaUser in default list
      modules: ['Create MediaUser', 'Playlist Create', 'Media Add'],
      newRole: {
        name: "",
        installation_type_id: "",
        selected_installation_type_ids: [],
        is_common: 0,
        status: '1'
      },
      editedRole: {
        name: "",
        installation_type_id: "",
        selected_installation_type_ids: [],
        is_common: 0,
        status: "1",
      },
      errors: {
        name: "",
      },
      selectedIndex: null,
      isAddModalVisible: false,
      isEditModalVisible: false,
      selectedRoles: [], // For selecting roles in add mode
      currentPage: 1,
      itemsPerPage: 10,
      selectedLanguage: this.$i18n.locale,
    };
  },
  computed: {
    isSuperAdmin() {
      const role = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().trim();
      return ['superadmin', 'super admin', '1'].includes(role);
    },
    isMedicalType() {
      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }
      const instType = String(user.installation_type_name || localStorage.getItem('installationType') || localStorage.getItem('installation_type_name') || '').toLowerCase();
      const instTypeId = Number(user.installation_type_id || localStorage.getItem('installation_type_id') || localStorage.getItem('installationType'));
      return instTypeId === 2 || instType.includes('medical');
    },
    userInstallationTypeId() {
      return localStorage.getItem('installation_type_id') || 
             localStorage.getItem('installationType') || '';
    },
    totalPages() {
      return Math.ceil(this.roles.length / this.itemsPerPage);
    },
    paginatedRoles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.roles.slice(start, end);
    },
  },
  created() {
    this.getAllRoles();
    this.getAllModules();
    this.fetchInstallationTypes();
  },
  methods: {
    getModuleDisplayName(mod) {
      if (!mod) return '';
      const code = this.getModuleCode(mod);
      if (code.startsWith('branch') || code.startsWith('department')) {
        return this.isMedicalType ? 'Departments' : 'Branches';
      }
      return mod.module_name || mod.name || mod;
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
    },
    getTypeName(type) {
      if (!type) return '';
      if (typeof type === 'string') return type;
      return type.name || type.installation_type_name || type.installation_name || type.type_name || type.title || type.code || `Type #${type.id}`;
    },
    async onInstallationTypesChange(selectedIds) {
      if (!selectedIds || (Array.isArray(selectedIds) && selectedIds.length === 0)) {
        this.modules = [...(this.allModulesCache || [])];
        return;
      }
      const firstId = Array.isArray(selectedIds) ? selectedIds[0] : selectedIds;
      await this.onInstallationTypeChange(firstId);
    },
    async fetchInstallationTypes() {
      if (this.installationTypes && this.installationTypes.length > 0) {
        return this.installationTypes;
      }
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        const res = await axios.get(`${baseUrl}/v1/admin/installation-types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data;
        if (Array.isArray(data)) {
          this.installationTypes = data;
        } else if (data && Array.isArray(data.data)) {
          this.installationTypes = data.data;
        } else if (data && Array.isArray(data.installationTypes)) {
          this.installationTypes = data.installationTypes;
        } else if (data && Array.isArray(data.result)) {
          this.installationTypes = data.result;
        }
        console.log("Installation types loaded in RoleTable:", this.installationTypes);
      } catch (e) {
        console.log("Error fetching installation types:", e);
      }
      return this.installationTypes;
    },
    async onInstallationTypeChange(installationTypeId) {
      if (!installationTypeId) return;

      if (!this.installationTypes || this.installationTypes.length === 0) {
        await this.fetchInstallationTypes();
      }

      const selectedType = (this.installationTypes || []).find(t => t.id == installationTypeId);
      let allowedIds = [];
      let allowedNames = [];

      if (selectedType) {
        let rawMods = selectedType.module_ids || selectedType.modules || selectedType.installation_type_modules || selectedType.permissions || [];
        if (typeof rawMods === 'string') {
          try {
            rawMods = JSON.parse(rawMods);
          } catch (e) {
            if (rawMods.includes(',')) {
              rawMods = rawMods.split(',').map(s => s.trim());
            } else if (!isNaN(rawMods) && rawMods.trim() !== '') {
              rawMods = [Number(rawMods)];
            } else {
              rawMods = [];
            }
          }
        }

        if (Array.isArray(rawMods)) {
          rawMods.forEach(m => {
            if (typeof m === 'number' || (typeof m === 'string' && !isNaN(m) && m.trim() !== '')) {
              allowedIds.push(Number(m));
            } else if (typeof m === 'object' && m !== null) {
              if (m.id || m.module_id) allowedIds.push(Number(m.id || m.module_id));
              if (m.module_name || m.name || m.code) {
                allowedNames.push(String(m.module_name || m.name || m.code).toLowerCase().replace(/[^a-z0-9]/g, ''));
              }
            } else if (typeof m === 'string') {
              allowedNames.push(m.toLowerCase().replace(/[^a-z0-9]/g, ''));
            }
          });
        }
      }

      const hasTenantBranchAccess = hasPermission('Branches', 'read') || 
                                    hasPermission('Departments', 'read') || 
                                    hasPermission('branches', 'read') || 
                                    hasPermission('departments', 'read');

      // Also incorporate tenant session allowed modules from all localStorage sources for non-superadmins
      if (!this.isSuperAdmin) {
        const storageKeys = ['allowedModules', 'userModules', 'permissions', 'modules', 'role_permissions'];
        storageKeys.forEach(key => {
          try {
            const rawVal = localStorage.getItem(key);
            if (!rawVal) return;
            let parsed = JSON.parse(rawVal);
            if (Array.isArray(parsed)) {
              parsed.forEach(sm => {
                if (!sm) return;
                if (typeof sm === 'number' || (typeof sm === 'string' && !isNaN(sm) && String(sm).trim() !== '')) {
                  allowedIds.push(Number(sm));
                } else if (typeof sm === 'object' && sm !== null) {
                  if (sm.id || sm.module_id) allowedIds.push(Number(sm.id || sm.module_id));
                  const name = sm.code || sm.module_name || sm.name || sm.slug || '';
                  if (name) allowedNames.push(String(name).toLowerCase().replace(/[^a-z0-9]/g, ''));
                } else if (typeof sm === 'string') {
                  let str = sm.toLowerCase().replace(/[^a-z0-9]/g, '');
                  if (str.includes(':')) str = str.split(':')[0];
                  if (str) allowedNames.push(str);
                }
              });
            }
          } catch (e) {
            // ignore parse failure
          }
        });

        try {
          const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
          const userObj = JSON.parse(userStr);
          const uPerms = userObj.permissions || userObj.modules || (userObj.role && (userObj.role.permissions || userObj.role.modules)) || [];
          if (Array.isArray(uPerms)) {
            uPerms.forEach(p => {
              if (!p) return;
              if (typeof p === 'number' || (typeof p === 'string' && !isNaN(p) && String(p).trim() !== '')) {
                allowedIds.push(Number(p));
              } else if (typeof p === 'object' && p !== null) {
                if (p.id || p.module_id) allowedIds.push(Number(p.id || p.module_id));
                const name = p.code || p.module_name || p.name || p.slug || '';
                if (name) allowedNames.push(String(name).toLowerCase().replace(/[^a-z0-9]/g, ''));
              } else if (typeof p === 'string') {
                let str = p.toLowerCase().replace(/[^a-z0-9]/g, '');
                if (str.includes(':')) str = str.split(':')[0];
                if (str) allowedNames.push(str);
              }
            });
          }
        } catch (e) {
          // ignore
        }
      }

      if (hasTenantBranchAccess) {
        allowedIds.push(6, 66);
        allowedNames.push('branches', 'branch', 'departments', 'department', 'branchmanagement', 'departmentmanagement');
      }

      if (this.allModulesCache && this.allModulesCache.length > 0) {
        if (allowedIds.length > 0 || allowedNames.length > 0) {
          const isAllowedMatch = (m) => {
            const mId = Number(m.id || m.module_id);
            const mName = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

            const isBranchOrDept = (s) => s.startsWith('branch') || s.startsWith('department');

            if (isBranchOrDept(mName) && hasTenantBranchAccess) {
              return true;
            }

            if (!isNaN(mId) && allowedIds.includes(mId)) return true;
            if (mName && allowedNames.includes(mName)) return true;

            // Branch & Department stem matching (handles 'Branch Management' <-> 'branches' <-> 'branch' <-> 'department' <-> 'departments')
            if (isBranchOrDept(mName) && allowedNames.some(a => isBranchOrDept(a))) {
              return true;
            }

            // Singular / Plural matching
            if (mName.endsWith('s') && allowedNames.includes(mName.slice(0, -1))) return true;
            if (allowedNames.some(a => a.endsWith('s') && a.slice(0, -1) === mName)) return true;

            // Direct check against hasPermission utility
            if (hasPermission(m.code || m.module_name || m.name, 'read')) {
              return true;
            }

            return false;
          };

          let filtered = this.allModulesCache.filter(isAllowedMatch);

          if (hasTenantBranchAccess) {
            const hasBranch = filtered.some(m => {
              const c = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
              return c.startsWith('branch') || c.startsWith('department');
            });
            if (!hasBranch) {
              const branchMod = (this.allModulesCache || []).find(m => {
                const c = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                return c.startsWith('branch') || c.startsWith('department');
              }) || {
                id: 6,
                module_id: 6,
                name: 'Branches',
                module_name: 'Branches',
                code: 'branches'
              };
              filtered.push(branchMod);
            }
          }

          if (filtered.length > 0) {
            this.modules = filtered;
            console.log(`Modules filtered dynamically for Installation Type ${installationTypeId}:`, this.modules);
            return;
          }
        }
        this.modules = [...this.allModulesCache];
        if (hasTenantBranchAccess) {
          const hasBranch = this.modules.some(m => {
            const c = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            return c.startsWith('branch') || c.startsWith('department');
          });
          if (!hasBranch) {
            this.modules.push({
              id: 6,
              module_id: 6,
              name: 'Branches',
              module_name: 'Branches',
              code: 'branches'
            });
          }
        }
      }
    },
    isProtectedFromDelete(role) {
      if (!role || !role.name) return false;
      const roleName = String(role.name).toLowerCase().replace(/[^a-z0-9]/g, '');
      const protectedRoles = ['admin', 'superadmin'];
      return protectedRoles.includes(roleName);
    },
    isAdminOrSuperAdmin(role) {
      return this.isProtectedFromDelete(role);
    },
    getAllRoles() {
      if (this.isSuperAdmin) {
        RolesDataService.getAll('')
          .then(response => {
            const res = response.data;
            this.roles = Array.isArray(res) ? res : (res.data || res.roles || []);
          })
          .catch(e => {
            console.log("Error fetching roles for SuperAdmin:", e);
          });
      } else {
        const instTypeId = this.userInstallationTypeId;
        const queryParam = instTypeId ? `?installation_type_id=${instTypeId}&include_common=1` : '';

        RolesDataService.getAll(queryParam)
          .then(response => {
            const res = response.data;
            let roleList = Array.isArray(res) ? res : (res.data || res.roles || []);

            if ((!roleList || roleList.length === 0) && queryParam) {
              RolesDataService.getAll('').then(allRes => {
                const allData = allRes.data;
                this.roles = Array.isArray(allData) ? allData : (allData.data || allData.roles || []);
              }).catch(err => console.log("Fallback roles fetch error:", err));
            } else {
              this.roles = roleList;
            }
          })
          .catch(e => {
            console.log("Error fetching roles for Tenant Admin:", e);
            RolesDataService.getAll('').then(allRes => {
              const allData = allRes.data;
              this.roles = Array.isArray(allData) ? allData : (allData.data || allData.roles || []);
            }).catch(err => console.log(err));
          });
      }
    },
    async getAllModules(){
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.VUE_APP_API_BASE_URL || '';
        const res = await axios.get(`${baseUrl}/v1/admin/get-modules`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const apiData = res.data;
        const apiModules = Array.isArray(apiData) ? apiData : (apiData?.data || apiData?.modules || []);
        this.modules = apiModules.filter(m => {
          const code = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          return !['installationtype', 'tenants'].includes(code);
        });

        // Ensure Branches / Departments module is present if omitted by the get-modules database endpoint
        const hasBranchInList = this.modules.some(m => {
          const c = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          return c.startsWith('branch') || c.startsWith('department');
        });
        if (!hasBranchInList) {
          this.modules.push({
            id: 6,
            module_id: 6,
            name: 'Branches',
            module_name: 'Branches',
            code: 'branches'
          });
        }

        this.allModulesCache = [...this.modules];
        console.log("Modules loaded dynamically for roles:", this.modules);

        if (!this.isSuperAdmin && this.userInstallationTypeId) {
          await this.onInstallationTypeChange(this.userInstallationTypeId);
        }
      } catch (e) {
        console.error("Error fetching modules dynamically in RoleTable:", e);
        this.modules = [];
        this.allModulesCache = [];
      }
    },
    getModuleCode(mod) {
      if (typeof mod === 'object' && mod !== null) {
        const raw = mod.code || mod.module_name || mod.name || '';
        return String(raw).toLowerCase().replace(/[^a-z0-9]/g, '');
      }
      return String(mod).toLowerCase().replace(/[^a-z0-9]/g, '');
    },
    isActionChecked(mod, action) {
      const code = this.getModuleCode(mod);
      const permKey = `${code}:${action}`;
      if (this.selectedRoles.includes(permKey) || this.selectedRoles.includes(`${code}:*`)) return true;

      // Handle branch/department synonym matching
      if (code.startsWith('branch') || code.startsWith('department')) {
        return this.selectedRoles.some(p => {
          const parts = String(p).split(':');
          const modPart = parts[0].toLowerCase();
          const actPart = parts[1] || '';
          return (modPart.startsWith('branch') || modPart.startsWith('department')) && 
                 (actPart === action || actPart === '*');
        });
      }
      return false;
    },
    toggleAction(mod, action) {
      const code = this.getModuleCode(mod);
      const permKey = `${code}:${action}`;
      const modId = typeof mod === 'object' ? (mod.id || mod.module_id) : mod;

      // Expand wildcard if previously set so actions can be individually toggled
      if (this.selectedRoles.includes(`${code}:*`)) {
        this.selectedRoles = this.selectedRoles.filter(p => p !== `${code}:*`);
        ['read', 'create', 'update', 'delete'].forEach(act => {
          const key = `${code}:${act}`;
          if (!this.selectedRoles.includes(key)) {
            this.selectedRoles.push(key);
          }
        });
      }

      // Clean out raw legacy IDs or legacy names for this module without removing other actions
      this.selectedRoles = this.selectedRoles.filter(p => {
        if (!String(p).includes(':')) {
          const pMod = String(p).split(':')[0];
          if (code === 'tokens' && (pMod === 'tokenmanagement' || pMod === 'token')) return false;
          if (code === 'counter' && (pMod === 'countermanagement' || pMod === 'counters')) return false;
          if (code === 'users' && (pMod === 'usermanagement' || pMod === 'user')) return false;
          if (code === 'services' && pMod === 'servicesprefix') return false;
          if ((code.startsWith('branch') || code.startsWith('department')) && (pMod.startsWith('branch') || pMod.startsWith('department'))) return false;
          return p !== modId && p !== mod && p !== code;
        }
        return true;
      });

      const idx = this.selectedRoles.indexOf(permKey);
      if (idx > -1) {
        this.selectedRoles.splice(idx, 1);
      } else {
        this.selectedRoles.push(permKey);
      }
    },
    isModuleFullyChecked(mod) {
      const actions = ['read', 'create', 'update', 'delete'];
      return actions.every(act => this.isActionChecked(mod, act));
    },
    toggleAllModuleActions(mod) {
      const code = this.getModuleCode(mod);
      const actions = ['read', 'create', 'update', 'delete'];
      const allChecked = this.isModuleFullyChecked(mod);
      
      const modId = typeof mod === 'object' ? (mod.id || mod.module_id) : mod;
      this.selectedRoles = this.selectedRoles.filter(p => {
        const pMod = String(p).split(':')[0];
        if (code === 'tokens' && (pMod === 'tokenmanagement' || pMod === 'token')) return false;
        if (code === 'counter' && (pMod === 'countermanagement' || pMod === 'counters')) return false;
        if (code === 'users' && (pMod === 'usermanagement' || pMod === 'user')) return false;
        if (code === 'services' && pMod === 'servicesprefix') return false;
        if ((code.startsWith('branch') || code.startsWith('department')) && (pMod.startsWith('branch') || pMod.startsWith('department'))) return false;
        return pMod !== code && p !== modId && p !== mod && p !== code && p !== `${code}:*`;
      });

      actions.forEach(act => {
        const permKey = `${code}:${act}`;
        const idx = this.selectedRoles.indexOf(permKey);
        if (allChecked) {
          if (idx > -1) this.selectedRoles.splice(idx, 1);
        } else {
          if (idx === -1) this.selectedRoles.push(permKey);
        }
      });
    },
    parseInstallationTypeIds(val) {
      if (val === undefined || val === null || val === '') return [];
      if (Array.isArray(val)) {
        return val.map(v => Number(v)).filter(n => !isNaN(n));
      }
      if (typeof val === 'number') {
        return !isNaN(val) ? [val] : [];
      }
      if (typeof val === 'string') {
        let str = val.trim();
        if (str.startsWith('[') && str.endsWith(']')) {
          try {
            const parsed = JSON.parse(str);
            if (Array.isArray(parsed)) {
              return parsed.map(v => Number(v)).filter(n => !isNaN(n));
            }
          } catch (e) {
            // ignore fallback
          }
        }
        return str.split(',')
          .map(s => s.trim())
          .filter(s => s !== '' && !isNaN(s))
          .map(Number);
      }
      return [];
    },
    addRole() {
      this.errors.name = "";

      if (!this.newRole.name.trim()) {
        this.errors.name = "Role name is required.";
        return;
      }

      let instTypePayload = this.newRole.selected_installation_type_ids;
      if (!this.isSuperAdmin) {
        instTypePayload = this.userInstallationTypeId;
      } else if (this.newRole.is_common) {
        instTypePayload = null;
      } else if (Array.isArray(instTypePayload)) {
        if (instTypePayload.length === 1) {
          instTypePayload = instTypePayload[0];
        } else if (instTypePayload.length === 0) {
          instTypePayload = null;
        }
      }

      const payload = {
        name: this.newRole.name,
        installation_type_id: instTypePayload,
        is_common: this.isSuperAdmin ? (this.newRole.is_common ? 1 : 0) : 0,
        permissions: JSON.stringify(this.selectedRoles),
        modules: JSON.stringify(this.selectedRoles)
      };
    
      RolesDataService.create(payload)
        .then(response => {
          console.log("Role created response:", response);
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'New Role Added successfully!',
            confirmButtonText: 'OK'
          });

          const createdRole = response.data?.role || response.data || {
            name: this.newRole.name,
            installation_type_id: instTypePayload,
            is_common: payload.is_common,
            status: this.newRole.status,
            permissions: this.selectedRoles
          };

          this.roles.push(createdRole);
          this.getAllRoles();
          this.resetForm('add');
        })
        .catch(e => {
          if (e.response && (e.response.status == 403 || e.response.status == 422 || e.response.status == 400)) {
            this.errors.name = e.response.data.message || (e.response.data.errors && e.response.data.errors[0] ? e.response.data.errors[0].message : "Validation error");
          } else {
            this.resetForm('add');

            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: "Failed to add role, Please try after some time!",
              confirmButtonText: 'OK'
            });
          }
        });
    },
    showModal(mode, index = null) {
      const hasTenantBranchAccess = hasPermission('Branches', 'read') || 
                                    hasPermission('Departments', 'read') || 
                                    hasPermission('branches', 'read') || 
                                    hasPermission('departments', 'read');

      const ensureBranchInModules = () => {
        if (hasTenantBranchAccess) {
          const hasBranch = (this.modules || []).some(m => {
            const c = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            return c.startsWith('branch') || c.startsWith('department');
          });
          if (!hasBranch) {
            const branchMod = (this.allModulesCache || []).find(m => {
              const c = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
              return c.startsWith('branch') || c.startsWith('department');
            }) || { id: 6, module_id: 6, name: 'Branches', module_name: 'Branches', code: 'branches' };
            this.modules.push(branchMod);
          }
        }
      };

      if (mode === 'add') {
        this.isAddModalVisible = true;
        this.selectedRoles = [];
        const userInstId = this.userInstallationTypeId;
        this.newRole = { 
          name: "", 
          installation_type_id: this.isSuperAdmin ? "" : userInstId, 
          selected_installation_type_ids: this.isSuperAdmin ? [] : (userInstId ? [Number(userInstId)] : []),
          is_common: 0,
          status: "1" 
        };
        if (userInstId) {
          this.onInstallationTypeChange(userInstId);
        } else if (!this.isSuperAdmin && this.userInstallationTypeId) {
          this.onInstallationTypeChange(this.userInstallationTypeId);
        }
        ensureBranchInModules();
      } 
      else if (mode === 'edit' && index !== null) {
        this.selectedIndex = index;
        const targetRole = this.roles[index];
        console.log("Editing role:", targetRole);
        const userInstId = targetRole.installation_type_id || targetRole.installation_id || "";
        const parsedInstIds = this.parseInstallationTypeIds(userInstId);
        const isCommonVal = targetRole.is_common ? 1 : 0;

        this.editedRole = { 
          ...targetRole, 
          installation_type_id: userInstId,
          selected_installation_type_ids: parsedInstIds,
          is_common: isCommonVal
        };

        if (parsedInstIds.length > 0) {
          this.onInstallationTypesChange(parsedInstIds);
        } else if (userInstId) {
          this.onInstallationTypeChange(userInstId);
        } else if (!this.isSuperAdmin && this.userInstallationTypeId) {
          this.onInstallationTypeChange(this.userInstallationTypeId);
        }

        ensureBranchInModules();

        this.isEditModalVisible = true;
        
        let rawPerms = targetRole.permissions || targetRole.modules || targetRole.RolePermissions || targetRole.role_permissions || [];
        
        let parseAttempts = 0;
        while (typeof rawPerms === 'string' && parseAttempts < 3) {
          parseAttempts++;
          try {
            let parsed = JSON.parse(rawPerms);
            if (parsed === rawPerms) break;
            rawPerms = parsed;
          } catch (e) {
            if (rawPerms.includes(',')) {
              rawPerms = rawPerms.split(',').map(s => s.trim());
            } else if (rawPerms.trim()) {
              rawPerms = [rawPerms.trim()];
            } else {
              rawPerms = [];
            }
            break;
          }
        }
        const normalized = [];
        
        if (Array.isArray(rawPerms)) {
          const normalizeCode = (codeStr) => {
            const clean = String(codeStr || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            const matched = (this.allModulesCache || []).find(m => {
              const mCode = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
              if (mCode === clean) return true;
              if ((mCode.startsWith('branch') || mCode.startsWith('department')) && (clean.startsWith('branch') || clean.startsWith('department'))) return true;
              return false;
            });
            return matched ? String(matched.code || matched.module_name || matched.name).toLowerCase().replace(/[^a-z0-9]/g, '') : clean;
          };

          const normalizeAction = (actStr) => {
            let act = String(actStr || '').toLowerCase().trim();
            if (act === 'view' || act === 'list' || act === 'view/list' || act === 'read' || act === 'get') return 'read';
            if (act === 'add' || act === 'create' || act === 'add/create' || act === 'post') return 'create';
            if (act === 'edit' || act === 'update' || act === 'edit/update' || act === 'write' || act === 'put') return 'update';
            if (act === 'delete' || act === 'remove' || act === 'destroy') return 'delete';
            if (act === '*' || act === 'all') return '*';
            return act;
          };

          const allActions = ['read', 'create', 'update', 'delete'];

          rawPerms.forEach(p => {
            if (typeof p === 'string') {
              let str = p.trim();
              if (str.includes(':')) {
                const parts = str.split(':');
                const cleanMod = normalizeCode(parts[0]);
                const rawAct = parts[1].toLowerCase().trim();
                const normAct = normalizeAction(rawAct);

                if (normAct === '*') {
                  allActions.forEach(a => normalized.push(`${cleanMod}:${a}`));
                } else {
                  normalized.push(`${cleanMod}:${normAct}`);
                }
              } else {
                const cleanCode = normalizeCode(str);
                if (cleanCode) {
                  allActions.forEach(a => normalized.push(`${cleanCode}:${a}`));
                }
              }
            } else if (typeof p === 'number') {
              const found = (this.allModulesCache || []).find(m => Number(m.id || m.module_id) === Number(p));
              if (found) {
                const cleanCode = normalizeCode(found.code || found.module_name || found.name);
                allActions.forEach(a => normalized.push(`${cleanCode}:${a}`));
              }
            } else if (typeof p === 'object' && p !== null) {
              const name = p.module_name || p.name || p.code || p.title || '';
              const cleanCode = normalizeCode(name);
              if (Array.isArray(p.actions) && p.actions.length > 0) {
                p.actions.forEach(act => {
                  const normAct = normalizeAction(act);
                  if (normAct === '*') {
                    allActions.forEach(a => normalized.push(`${cleanCode}:${a}`));
                  } else {
                    normalized.push(`${cleanCode}:${normAct}`);
                  }
                });
              } else if (cleanCode) {
                allActions.forEach(a => normalized.push(`${cleanCode}:${a}`));
              }
            }
          });
        }
        this.selectedRoles = Array.from(new Set(normalized));
        console.log("DEBUG: TargetRole inside showModal:", JSON.parse(JSON.stringify(targetRole)));
        console.log("DEBUG: rawPerms after parsing:", rawPerms);
        console.log("DEBUG: Normalized selectedRoles:", this.selectedRoles);
      }
    },
    roleHasModule(module) {
      return this.selectedRoles.includes(module);
    },
    hideModal(mode) {
      if (mode === 'add') {
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.isEditModalVisible = false;
      }
      this.errors.name = "";
      this.selectedIndex = null;
    },
    updateRole() {
      this.errors.name = "";

      if (!this.editedRole.name.trim()) {
        this.errors.name = "Role name is required.";
        return;
      }

      let instTypePayload = this.editedRole.selected_installation_type_ids;
      if (!this.isSuperAdmin) {
        instTypePayload = this.editedRole.installation_type_id || this.userInstallationTypeId;
      } else if (this.editedRole.is_common) {
        instTypePayload = null;
      } else if (Array.isArray(instTypePayload)) {
        if (instTypePayload.length === 1) {
          instTypePayload = instTypePayload[0];
        } else if (instTypePayload.length === 0) {
          instTypePayload = null;
        }
      }

      const permissionsToSubmit = Array.isArray(this.selectedRoles) ? this.selectedRoles : [];

      const updatedRole = { 
        ...this.editedRole, 
        installation_type_id: instTypePayload,
        is_common: this.isSuperAdmin ? (this.editedRole.is_common ? 1 : 0) : 0,
        permissions: JSON.stringify(permissionsToSubmit),
        modules: JSON.stringify(permissionsToSubmit)
      }; 

      RolesDataService.update(this.roles[this.selectedIndex].id, updatedRole)
        .then(response => {
          //this.teams = response.data.role;
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Role updated successfully!',
            confirmButtonText: 'OK'
          });
          console.log(response.data.role);
          this.roles.splice(this.selectedIndex, 1, updatedRole);
          this.getAllRoles();

          // Update active session permissions immediately if editing current user's role
          const currentUserRole = String(localStorage.getItem('userRole') || '').toLowerCase().replace(/\s+/g, '');
          const updatedRoleName = String(updatedRole.name || '').toLowerCase().replace(/\s+/g, '');
          if (currentUserRole === updatedRoleName || currentUserRole === 'test' || updatedRoleName === 'test') {
            localStorage.setItem('userModules', JSON.stringify(permissionsToSubmit));
            localStorage.setItem('allowedModules', JSON.stringify(permissionsToSubmit));
            window.dispatchEvent(new Event('storage'));
          }

          this.resetForm('edit');
        })
        .catch(e => {
          console.log(e);
          if (e.response && (e.response.status == 403 || e.response.status == 422 || e.response.status == 400)) {
            this.errors.name = e.response.data.message || (e.response.data.errors && e.response.data.errors[0] ? e.response.data.errors[0].message : "Validation error");
          } else {
            this.resetForm('edit');

            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: "Failed to update role, Please try after some time!",
              confirmButtonText: 'OK'
            });
          }
        });
    },
    confirmDelete(index, id) {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteRole(index, id);
          Swal.fire(
            'Deleted!',
            'The Role has been deleted.',
            'success'
          );
        }
      });
    },
    deleteRole(index, id) {
      RolesDataService.delete(id)
        .then(response => {
          console.log(response);
          this.roles.splice(index, 1);
        })
        .catch(e => {
          console.log(e);
        });
    },
    resetForm(mode) {
      if (mode === 'add') {
        this.newRole = { name: "", status: "Active" };
        this.selectedRoles = [];
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.selectedIndex = null;
        this.isEditModalVisible = false;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style scoped>
/* ==========================
   DYNAMIC THEME SYNC
========================== */
.btn-theme-primary {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: 1px solid var(--bs-primary) !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-theme-primary:hover {
  filter: brightness(0.9);
}

.btn-primary {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.btn-outline-primary {
  border: 1px solid var(--bs-primary) !important;
  color: var(--bs-primary) !important;
  background: transparent;
}

.btn-outline-primary:hover {
  background-color: var(--bs-primary) !important;
  color: white !important;
}

.theme-icon-bg {
  background-color: var(--bs-primary) !important;
}

.btn-modal-close {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ==========================
   PAGINATION SYNC
========================== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  padding-bottom: 1rem;
}

.pagination button {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  margin: 0.5rem; /* Updated for wrapping */
  font-size: 12px;
  font-weight: 800 !important;
  text-transform: uppercase;
  cursor: pointer;
  transition: 0.2s;
}

.pagination button:disabled {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

.pagination button.active {
  background-color: #ffffff !important;
  color: var(--bs-primary) !important;
  border: 2px solid var(--bs-primary) !important;
}

/* ==========================
   EXISTING SWITCH LOGIC
========================== */
.langdrop {
  width: 130px;
  margin-left: 87%;
}

@media (min-width:300px) and (max-width:550px) {
  .langdrop {
    width: 130px;
    margin-left: 57%;
  }
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked+.slider {
  background-color: var(--bs-primary) !important; /* Theme Color instead of Green */
}

input:checked+.slider:before {
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.table th,
.table td {
  text-align: left;
  padding: 12px;
}

.modal.fade.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  margin-top: 10%;
}

.fa-edit,
.fa-trash-alt {
  font-size: 1rem;
}

/* --- ADDED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  .card-header {
    padding-bottom: 20px !important;
  }
  .modal-dialog {
    margin: 0.5rem;
  }
}
</style>